import {
    Component,
    Input,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { TranslateDirective, TranslatePipe } from "@ngx-translate/core";
import _ from "lodash";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "~app/common/validation/validationContext";
import { DirtyTabComponent } from "~app/features/regelverk-viol/dirtyTabComponent";
import { JlpLeverantorsorderSearchModel } from "../../common/models/jlp-leverantorsoder-model";
import { IJlpResultSettings, INamedSokfilter } from "../../common/models/jlp-model";
import { GridPeriod, IOrderLeverantorsorderSearchResponse } from "../../common/models/justeraLeveransplanModel";
import { JlpPeriodService } from "../../common/services/jlp-period.service";
import { JusteraLeveransplanFilterService } from "../../common/services/justera-leveransplan-filter.service";
import { JusteraLeveransplanService } from "../../common/services/justera-leveransplan.service";
import { LeverantorsorderValidation } from "../../common/validation/jlp-validation";
import { JlpSavedSearchesComponent } from "../../components/jlp-saved-searches/jlp-saved-searches.component";
import { JlpLeverantorsorderResultComponent } from "./jlp-leverantorsorder-result/jlp-leverantorsorder-result.component";

@Component({
    selector: "rask-jlp-leverantorsorder",
    templateUrl: "./jlp-leverantorsorder.component.html",
    styleUrls: ["./jlp-leverantorsorder.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        JlpSavedSearchesComponent,
        FormsModule,
        ReactiveFormsModule,
        MultiSelectorComponent,
        TreeSelectorComponent,
        DatepickerComponent,
        ButtonComponent,
        JlpLeverantorsorderResultComponent,
        TranslateDirective,
        TranslatePipe
    ]
})
export class JlpLeverantorsorderComponent extends DirtyTabComponent implements OnInit {

    readonly WIKSparade = "JusteraLeveransplan.leverantorsorderfilter.sparade";
    readonly name = "leverantorsorder";

    basdata: any = null;
    @Input() canEdit: boolean;
    currentData: IOrderLeverantorsorderSearchResponse;
    leverantorsorderForm = new UntypedFormGroup({
        kunder: new UntypedFormControl([]),
        omraden: new UntypedFormControl([]),
        mottagningsplatser: new UntypedFormControl([]),
        handelssortimentsgrupper: new UntypedFormControl([]),
        periodFrom: new UntypedFormControl(),
        periodTom: new UntypedFormControl(),
        avtalsansvariga: new UntypedFormControl([]),
        leverantorer: new UntypedFormControl([])
    });
    model: JlpLeverantorsorderSearchModel;
    noOrderrows = false;
    period: GridPeriod;
    @ViewChild(JlpLeverantorsorderResultComponent) results: JlpLeverantorsorderResultComponent;
    settings: IJlpResultSettings;
    showResults = false;
    sokningarLev: INamedSokfilter[];
    triggerSparaFilter: any;
    validationContext: ValidationContext;

    private readonly WIK = "JusteraLeveransplan.leverantorsorderfilter";
    private readonly busyService = inject(BusyService);
    private readonly dirtyFormServiceRef: DirtyFormService;
    private readonly formBinder = inject(FormBinderService);
    private readonly jlpPeriodService = inject(JlpPeriodService);
    private readonly jlpService = inject(JusteraLeveransplanService);
    private readonly justeraLeveransplanFilterService = inject(JusteraLeveransplanFilterService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        const dirtyFormService = inject(DirtyFormService);

        super(dirtyFormService);
        this.dirtyFormServiceRef = dirtyFormService;
    }

    canSave() {
        this.results.isDirty();
    }

    formatter = (result: ITypeAheadResult) => result ? result.namn : undefined;

    initPage() {
        this.jlpService.getJusteraLeveransplanBasdata().subscribe((basdata: any) => {
            this.model = _.merge(new JlpLeverantorsorderSearchModel(), this.webinstallningarService.get<JlpLeverantorsorderSearchModel>(this.WIK));
            this.basdata =
                this.justeraLeveransplanFilterService.setFilter(basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
            this.setForm();
            this.validationContext = LeverantorsorderValidation.init(this.model);
        });
    }

    isDirtyCheck(): boolean {
        if (this.results === undefined) {
            return false;
        }
        return this.results.isDirty();
    }

    ngOnInit() {
        this.initPage();
    }

    outputFormatter = (result: ITypeAheadResult) => result ? [result] : undefined;

    reset() {
        this.leverantorsorderForm.controls.kunder.setValue([]);
        this.leverantorsorderForm.controls.omraden.setValue([]);
        this.leverantorsorderForm.controls.mottagningsplatser.setValue([]);
        this.leverantorsorderForm.controls.handelssortimentsgrupper.setValue([]);
        this.leverantorsorderForm.controls.periodFrom.setValue(null);
        this.leverantorsorderForm.controls.periodTom.setValue(null);
        this.leverantorsorderForm.controls.avtalsansvariga.setValue([]);
        this.leverantorsorderForm.controls.leverantorer.setValue([]);
        this.setModel(this.leverantorsorderForm.value);
        this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
        this.setForm();
    }

    restoreChanges() {
        this.search();
    }

    saveChanges() {
        this.results.save();
    }

    saveSearchParams() {
        this.leverantorsorderForm.value.handelssortimentsgrupper = this.justeraLeveransplanFilterService.getSelectedHandelssortiment(this.basdata.handelssortiment);

        if (ObjectUtils.hasValue(this.leverantorsorderForm.get("periodFrom").value)) {
            this.leverantorsorderForm.value.periodFrom = this.jlpPeriodService.addFirstDayOfMonth(this.leverantorsorderForm.get("periodFrom").value);
        }

        if (ObjectUtils.hasValue(this.leverantorsorderForm.get("periodTom").value)) {
            this.leverantorsorderForm.value.periodTom = this.jlpPeriodService.getLastDayOfMonth(this.leverantorsorderForm.get("periodTom").value);
        }

        this.triggerSparaFilter = Object.assign({}, this.leverantorsorderForm.value);
    }

    search() {
        const searchFunc = () => {
            this.noOrderrows = false;
            this.currentData = null;
            this.setModel(this.leverantorsorderForm.value);
            this.validationContext.scope(ValidationScopes.sok, ValidationErrorLevels.error).subscribe(scope => {
                if (this.validationContext.validate(true)) {
                    this.leverantorsorderForm.value.handelssortimentsgrupper = this.justeraLeveransplanFilterService.getSelectedHandelssortiment(this.basdata.handelssortiment);

                    this.leverantorsorderForm.value.periodFrom = this.jlpPeriodService.addFirstDayOfMonth(this.leverantorsorderForm.get("periodFrom").value);
                    this.leverantorsorderForm.value.periodTom = this.jlpPeriodService.getLastDayOfMonth(this.leverantorsorderForm.get("periodTom").value);

                    this.webinstallningarService.set(this.WIK, this.leverantorsorderForm.value);
                    const query = this.jlpService.searchForLeverantorsorder(this.leverantorsorderForm.value);
                    this.busyService.addWork(query).subscribe((res: IOrderLeverantorsorderSearchResponse) => {
                        this.showResults = true;
                        if (res.leverantorsorderrader.length === 0) {
                            this.noOrderrows = true;
                        } else {
                            this.settings = {
                                passeradeAntalArbetsdagarInnevarandeManad: res.passeradeAntalArbetsdagarInnevarandeManad,
                                totaltAntalArbetsdagarInnevarandeManad: res.totaltAntalArbetsdagarInnevarandeManad,
                                periodFrom: this.leverantorsorderForm.get("periodFrom").value,
                                periodTom: this.leverantorsorderForm.get("periodTom").value
                            };
                            this.period = this.jlpPeriodService.getPeriods(this.settings.periodFrom, this.settings.periodTom);
                            this.currentData = res;
                        }
                    });
                }
            });
        };

        if (this.isDirtyCheck()) {
            this.dirtyFormServiceRef.dirtyFormModal(
                () => searchFunc(),
                () => {
                    this.saveChanges();
                    searchFunc();
                },
                () => undefined);
        } else {
            searchFunc();
        }
    }

    searchForAvtalsansvarig = (searchString: string) => this.jlpService.searchForAvatalsansvarigTypeahead(searchString);

    searchForCustomer = (searchString: string) => this.jlpService.searchForKundTypeAhead(searchString);

    searchForLeverantor = (searchString: string) => this.jlpService.searchForLeverantorTypeAhead(searchString);

    searchForMottagningsplatsTypeAhead = (searchString: string) => this.jlpService.searchForMottagningsplatsTypeAhead(searchString, null);

    setSokning(namedSokFilter: INamedSokfilter) {
        if (namedSokFilter) {
            this.reset();
            this.setModel(namedSokFilter);
            this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
            this.setForm();
        }
    }

    private setForm() {
        if (!ObjectUtils.hasValue(this.leverantorsorderForm)) {
            this.leverantorsorderForm = this.formBinder.CreateGroup(this.model);
        } else {
            this.leverantorsorderForm.controls.kunder.setValue(this.model.kunder);
            this.leverantorsorderForm.controls.mottagningsplatser.setValue(this.model.mottagningsplatser);
            this.leverantorsorderForm.controls.periodFrom.setValue(this.model.periodFrom);
            this.leverantorsorderForm.controls.periodTom.setValue(this.model.periodTom);
            this.leverantorsorderForm.controls.avtalsansvariga.setValue(this.model.avtalsansvariga);
            this.leverantorsorderForm.controls.leverantorer.setValue(this.model.leverantorer);
        }
    }

    private setModel(values: any) {
        this.model.kunder = values.kunder;
        this.model.mottagningsplatser = values.mottagningsplatser;
        this.model.periodFrom = values.periodFrom;
        this.model.periodTom = values.periodTom;
        this.model.avtalsansvariga = values.avtalsansvariga;
        this.model.leverantorer = values.leverantorer;
        this.model.omraden = values.omraden ?? [];
        this.model.handelssortimentsgrupper = values.handelssortimentsgrupper ?? [];
    }
}
